<script setup>
// Set signin vs signup based on url param
// const isSignUp = ref(useRoute().query.signup == 'true');

// Add watcher so if url param changes the page is reloaded. This is needed because clicking the navbar buttons from this page didn't properly reload the page
watch(() => useRoute().query.signup, () => {
  window.location.reload();
});

// Municipality logo logic
const municipality = useCookie("municipality");
const supportedMunicipalityLogos = ["mm", "crowsnest"];
const useMunicipalityLogo = computed(() => {
  if (
    municipality.value &&
    supportedMunicipalityLogos.includes(municipality.value)
  ) {
    return true;
  }
  return false;
});
</script>

<template>
  <div
    class="min-h-screen relative flex justify-center items-center overflow-hidden bg-gradient-to-br from-gray-50 via-gray-100 to-white">
    <!-- Enhanced Animated Background Elements -->
    <div class="backdrop-container absolute inset-0 pointer-events-none">
      <!-- Original bubbles -->
      <div class="bubble-container">
        <div class="bubble bubble-1"></div>
        <div class="bubble bubble-2"></div>
        <div class="bubble bubble-3"></div>
        <div class="bubble bubble-4"></div>
        <div class="bubble bubble-5"></div>
        <div class="bubble bubble-6"></div>

        <!-- Decorative blobs -->
        <div class="blob blob-1"></div>
        <div class="blob blob-2"></div>
      </div>

      <!-- Particles effect -->
      <div class="particles-container">
        <div class="particle particle-1"></div>
        <div class="particle particle-2"></div>
        <div class="particle particle-3"></div>
        <div class="particle particle-4"></div>
        <div class="particle particle-5"></div>
        <div class="particle particle-6"></div>
        <div class="particle particle-7"></div>
        <div class="particle particle-8"></div>
        <div class="particle particle-9"></div>
        <div class="particle particle-10"></div>
        <div class="particle particle-11"></div>
        <div class="particle particle-12"></div>
      </div>

      <!-- Geometric accent elements -->
      <div class="geometric-accents">
        <div class="accent-circle"></div>
        <div class="accent-square"></div>
        <div class="accent-triangle"></div>
        <div class="accent-line line-1"></div>
        <div class="accent-line line-2"></div>
        <div class="accent-line line-3"></div>
      </div>

      <!-- Subtle pattern overlay -->
      <div class="pattern-overlay"></div>

      <!-- Dynamic animated waves -->
      <div class="wave-container">
        <div class="wave wave-1"></div>
        <div class="wave wave-2"></div>
      </div>
    </div>

    <!-- Main content -->
    <div class="w-full max-w-md px-4 py-8 relative z-10">
      <!-- Dual logos with modern treatment -->
      <div class="relative mb-8 text-center">
        <!-- Backdrop shape -->
        <div
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full bg-gradient-to-br from-municipality-primary/5 to-municipality-secondary/10 blur-lg animate-pulse-slow">
        </div>

        <!-- Creative logo arrangement -->
        <div class="logos-container relative z-10">
          <div class="flex flex-col items-center md:flex-row md:justify-center md:space-x-6">
            <!-- Main Siteability logo -->
            <div class="relative">
              <img src="/images/logo.png" alt="Siteability Logo" class="w-52 mx-auto" />
              <!-- Subtle connecting curve -->
              <div v-if="useMunicipalityLogo"
                class="hidden md:block absolute top-1/2 -right-8 w-16 h-1 bg-gradient-to-r from-municipality-primary/50 to-municipality-secondary/50 rounded-full transform -translate-y-1/2">
              </div>
            </div>

            <!-- Municipality logo (without circular frame) -->
            <div v-if="useMunicipalityLogo" class="mt-3 md:mt-0 flex items-center justify-center h-24">
              <img :src="`/images/municipality-logos/${municipality}.png`" :alt="`${municipality} Logo`"
                class="max-h-20 max-w-36 object-contain" />
            </div>
          </div>
        </div>

        <h2 class="text-2xl font-light text-gray-700 mt-4 relative z-10 text-nowrap">
          Learn what can be built on your property
        </h2>
      </div>

      <slot></slot>
    </div>
  </div>
</template>

<style scoped>
/* Original styles */
.bubble-container {
  overflow: hidden;
  opacity: 0.8;
}

@keyframes float-slow {

  0%,
  100% {
    transform: translate(0, 0) rotate(0deg);
  }

  25% {
    transform: translate(-5px, -15px) rotate(2deg);
  }

  50% {
    transform: translate(10px, 5px) rotate(-1deg);
  }

  75% {
    transform: translate(-15px, 10px) rotate(3deg);
  }
}

@keyframes float-medium {

  0%,
  100% {
    transform: translate(0, 0) rotate(0deg);
  }

  33% {
    transform: translate(15px, -10px) rotate(-2deg);
  }

  66% {
    transform: translate(-10px, 15px) rotate(1deg);
  }
}

@keyframes float-fast {

  0%,
  100% {
    transform: translate(0, 0) rotate(0deg);
  }

  50% {
    transform: translate(10px, -7px) rotate(5deg);
  }
}

.animate-pulse-slow {
  animation: pulse 6s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 0.3;
  }

  50% {
    opacity: 0.7;
  }
}

.bubble {
  position: absolute;
  border-radius: 50%;
  filter: blur(1px);
}

.bubble-1 {
  width: 300px;
  height: 300px;
  top: -100px;
  right: -50px;
  background: radial-gradient(circle at 30% 40%, varprimary-500/5%, varprimary-500/20%);
  animation: float-slow 20s ease-in-out infinite;
}

.bubble-2 {
  width: 250px;
  height: 250px;
  bottom: -50px;
  left: -80px;
  background: radial-gradient(circle at 70% 60%, var(--color-brand-secondary)/5%, var(--color-brand-secondary)/15%);
  animation: float-medium 18s ease-in-out infinite;
}

.bubble-3 {
  width: 180px;
  height: 180px;
  top: 15%;
  left: 10%;
  background: radial-gradient(circle at 30% 40%, varprimary-500/5%, varprimary-500/10%);
  animation: float-fast 15s ease-in-out infinite;
}

.bubble-4 {
  width: 120px;
  height: 120px;
  bottom: 25%;
  right: 15%;
  background: radial-gradient(circle at 30% 40%, var(--color-brand-secondary)/8%, var(--color-brand-secondary)/15%);
  animation: float-slow 22s ease-in-out infinite;
}

.bubble-5 {
  width: 100px;
  height: 100px;
  top: 10%;
  right: 20%;
  background: radial-gradient(circle at 30% 40%, var(--color-brand-secondary)/5%, varprimary-500/12%);
  animation: float-medium 25s ease-in-out infinite reverse;
}

.bubble-6 {
  width: 80px;
  height: 80px;
  bottom: 10%;
  left: 20%;
  background: radial-gradient(circle at 30% 40%, varprimary-500/8%, var(--color-brand-secondary)/15%);
  animation: float-fast 19s ease-in-out infinite;
}

.blob {
  position: absolute;
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  filter: blur(40px);
}

.blob-1 {
  width: 500px;
  height: 500px;
  top: -250px;
  left: -250px;
  background-color: varprimary-500;
  opacity: 0.03;
  animation: rotate 90s linear infinite;
}

.blob-2 {
  width: 400px;
  height: 400px;
  bottom: -200px;
  right: -200px;
  background-color: var(--color-brand-secondary);
  opacity: 0.04;
  animation: rotate 70s linear infinite reverse;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* New Backdrop Enhancement Styles */

/* Particles effect */
.particles-container {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.particle {
  position: absolute;
  background-color: varprimary-500;
  border-radius: 50%;
  opacity: 0.2;
  animation: float-particle 15s infinite linear;
}

.particle:nth-child(even) {
  background-color: var(--color-brand-secondary);
}

.particle-1 {
  width: 6px;
  height: 6px;
  top: 10%;
  left: 15%;
  animation-duration: 30s;
}

.particle-2 {
  width: 8px;
  height: 8px;
  top: 20%;
  left: 85%;
  animation-duration: 25s;
}

.particle-3 {
  width: 4px;
  height: 4px;
  top: 35%;
  left: 30%;
  animation-duration: 28s;
}

.particle-4 {
  width: 5px;
  height: 5px;
  top: 40%;
  left: 70%;
  animation-duration: 32s;
}

.particle-5 {
  width: 7px;
  height: 7px;
  top: 65%;
  left: 15%;
  animation-duration: 36s;
}

.particle-6 {
  width: 6px;
  height: 6px;
  top: 75%;
  left: 75%;
  animation-duration: 29s;
}

.particle-7 {
  width: 9px;
  height: 9px;
  top: 85%;
  left: 25%;
  animation-duration: 31s;
}

.particle-8 {
  width: 4px;
  height: 4px;
  top: 5%;
  left: 50%;
  animation-duration: 27s;
}

.particle-9 {
  width: 6px;
  height: 6px;
  top: 50%;
  left: 5%;
  animation-duration: 33s;
}

.particle-10 {
  width: 5px;
  height: 5px;
  top: 55%;
  left: 95%;
  animation-duration: 26s;
}

.particle-11 {
  width: 8px;
  height: 8px;
  top: 90%;
  left: 60%;
  animation-duration: 34s;
}

.particle-12 {
  width: 7px;
  height: 7px;
  top: 30%;
  left: 45%;
  animation-duration: 24s;
}

@keyframes float-particle {
  0% {
    transform: translateY(0) translateX(0);
    opacity: 0;
  }

  10% {
    opacity: 0.2;
  }

  90% {
    opacity: 0.15;
  }

  100% {
    transform: translateY(-150px) translateX(20px);
    opacity: 0;
  }
}

/* Geometric accent elements */
.geometric-accents {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.accent-circle {
  position: absolute;
  width: 50px;
  height: 50px;
  border: 1px solid varprimary-500;
  border-radius: 50%;
  top: 15%;
  right: 10%;
  opacity: 0.1;
  animation: spin 30s linear infinite;
}

.accent-square {
  position: absolute;
  width: 40px;
  height: 40px;
  border: 1px solid var(--color-brand-secondary);
  bottom: 20%;
  left: 8%;
  opacity: 0.1;
  animation: spin 25s linear infinite reverse;
}

.accent-triangle {
  position: absolute;
  width: 0;
  height: 0;
  border-left: 30px solid transparent;
  border-right: 30px solid transparent;
  border-bottom: 50px solid varprimary-500;
  top: 65%;
  right: 12%;
  opacity: 0.05;
  animation: float-slow 35s infinite;
}

.accent-line {
  position: absolute;
  height: 1px;
  background: linear-gradient(to right, transparent, varprimary-500, var(--color-brand-secondary), transparent);
  opacity: 0.1;
}

.line-1 {
  width: 30%;
  top: 25%;
  left: 5%;
  transform: rotate(10deg);
}

.line-2 {
  width: 20%;
  top: 45%;
  right: 10%;
  transform: rotate(-15deg);
}

.line-3 {
  width: 25%;
  bottom: 30%;
  left: 20%;
  transform: rotate(5deg);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* Pattern overlay */
.pattern-overlay {
  position: absolute;
  inset: 0;
  opacity: 0.03;
  background-image:
    repeating-linear-gradient(0deg, varprimary-500, varprimary-500 1px, transparent 1px, transparent 20px),
    repeating-linear-gradient(90deg, var(--color-brand-secondary), var(--color-brand-secondary) 1px, transparent 1px, transparent 20px);
  background-size: 20px 20px;
}

/* Wave animations */
.wave-container {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.wave {
  position: absolute;
  width: 200%;
  height: 150px;
  background: linear-gradient(to right, transparent, varprimary-500/5%, var(--color-brand-secondary)/5%, transparent);
  opacity: 0.1;
}

.wave-1 {
  bottom: 0;
  left: -50%;
  height: 150px;
  border-radius: 100%;
  animation: wave 30s infinite linear;
}

.wave-2 {
  bottom: -20px;
  left: -50%;
  height: 120px;
  border-radius: 100%;
  animation: wave 20s infinite linear reverse;
}

@keyframes wave {
  0% {
    transform: translateX(0) scale(1);
  }

  50% {
    transform: translateX(25%) scale(1.1);
  }

  100% {
    transform: translateX(50%) scale(1);
  }
}
</style>